import type { ReactNode } from "react";

import { useMemo, useRef } from "react";

import { Autocomplete } from "../../autocomplete";
import { Searchbar } from "../../searchbar";
import { Skeleton } from "../../skeleton";
import { SelectBase } from "../select-base";

import styles from "./autocomplete-input.module.css";

type AutocompleteInputProps<T extends { id: string }> = {
	description?: string;
	emptyState: ReactNode;
	errorMessage?: ReactNode;
	fetchNextPage: () => void;
	hasNextPage?: boolean;
	isDisabled?: boolean;
	isFetching: boolean;
	isFetchingNextPage?: boolean;
	isInvalid?: boolean;
	isReadOnly?: boolean;
	items: T[];
	label: string;
	menuItem: (item: T) => ReactNode;
	onChange: (id: null | string) => void;
	onSearch: (val: string) => void;
	placeholder: string;
	searchPlaceholder?: string;
	searchValue: string;
	/**
	 * The raw selected ID from the form field. Used to keep the display value
	 * stable while the resolved display name is being fetched.
	 */
	selectedId?: string;
	slotLeft?: ReactNode;
	value?: string;
};

export function AutocompleteInput<T extends { id: string }>({
	description,
	emptyState,
	errorMessage,
	fetchNextPage,
	hasNextPage,
	isDisabled,
	isFetching,
	isFetchingNextPage,
	isInvalid,
	isReadOnly,
	items,
	label,
	menuItem,
	onChange,
	onSearch,
	placeholder,
	searchPlaceholder,
	searchValue,
	selectedId,
	slotLeft,
	value,
}: AutocompleteInputProps<T>): ReactNode {
	const lastKnownValue = useRef<string | undefined>(undefined);

	const stableValue = useMemo(() => {
		if (value !== undefined) {
			lastKnownValue.current = value;
			return value;
		}
		if (selectedId) {
			return lastKnownValue.current;
		}
		lastKnownValue.current = undefined;
		return undefined;
	}, [value, selectedId]);

	return (
		<SelectBase
			description={description}
			errorMessage={errorMessage}
			isDisabled={isDisabled}
			isInvalid={isInvalid}
			isReadOnly={isReadOnly}
			label={label}
			onChange={onChange}
			placeholder={placeholder}
			slotLeft={slotLeft}
			value={stableValue}
		>
			{(updateValue) => {
				return (
					<Autocomplete
						emptyState={<div className={styles.empty}>{emptyState}</div>}
						fetchNextPage={fetchNextPage}
						hasNextPage={Boolean(hasNextPage)}
						isFetching={isFetching}
						isFetchingNextPage={Boolean(isFetchingNextPage)}
						items={items}
						loadingNewPageItem={
							<div className={styles.loading}>
								<Skeleton height={8} width={100} />
							</div>
						}
						menuItem={(item) => {
							return <div className={styles.option}>{menuItem(item)}</div>;
						}}
						onChange={updateValue}
						searchbar={
							<Searchbar
								autoFocus
								className={styles.searchbar}
								onChange={onSearch}
								placeholder={searchPlaceholder || "Search"}
								value={searchValue}
							/>
						}
					/>
				);
			}}
		</SelectBase>
	);
}
